// AG7 Store — API client for the FastAPI backend.
// All endpoints are prefixed with /api and reached via REACT_APP_BACKEND_URL.

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- SECURITY: httpOnly cookie based auth ----------------------------------
// Auth tokens used to live in localStorage which exposed them to any XSS. They
// now travel exclusively via an httpOnly `ag7_token` cookie set by the backend
// on login. Every fetch below opts into `credentials: "include"` so the browser
// attaches the cookie same-origin (preview + production are same-origin) or
// cross-origin (when configured via CORS_ORIGINS on the backend).
//
// The legacy `auth.getToken/setToken/clear` API is kept as a NO-OP shim so
// unrelated code that guards on `getToken()` doesn't have to be touched right
// now. Callers should migrate to the `user` state exposed by App.js — the
// cookie is authoritative and `/api/auth/me` decides whether a user is signed
// in on page load.
const _origFetch = (typeof window !== "undefined" ? window.fetch : globalThis.fetch).bind(
  typeof window !== "undefined" ? window : globalThis,
);
const fetch = (input, init = {}) => _origFetch(input, { credentials: "include", ...init });

export const auth = {
  setToken: (_t) => { /* handled by httpOnly cookie set by backend */ },
  getToken: () => null,
  clear: () => { /* cookie is cleared by POST /auth/logout */ },
};

// Kept for backwards compatibility with callers that still spread the result
// into their headers. Always returns {} now — the cookie carries the JWT.
const authHeaders = () => ({});

async function handle(res) {
  const text = await res.text();
  // Guard against non-JSON responses. In production the Emergent ingress
  // returns HTML pages on 502/504/maintenance windows; a naive JSON.parse
  // would explode with "Unexpected token '<'" and, worse, that promise
  // rejection would bubble into the login/session bootstrap path and log
  // the user out on every refresh. We now degrade gracefully: keep the
  // parsed payload when we can, fall back to a status-shaped error object
  // otherwise, and never surface the raw parser exception to the UI.
  let data = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch (parseErr) {
      // HTML from the ingress / gateway. Preserve enough context for logs
      // but hide the JSON-parse noise from users.
      console.warn("api.handle: non-JSON response", { status: res.status, snippet: text.slice(0, 120) });
      data = {
        detail: res.ok
          ? "Respuesta del servidor no válida. Intenta nuevamente."
          : `El servidor devolvió un error (${res.status}). Reintenta en unos segundos.`,
        _nonJson: true,
      };
    }
  }
  if (!res.ok) {
    // `detail` may be a plain string (legacy) OR a structured object
    // (new OCR pre-check gates). Preserve both — the object is available
    // on `err.data.detail`, the string message goes into `err.message`.
    let msg;
    if (typeof data.detail === "string") {
      msg = data.detail;
    } else if (data.detail && typeof data.detail === "object" && data.detail.message) {
      msg = String(data.detail.message);
    } else {
      msg = `HTTP ${res.status}`;
    }
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  // ok=true but the body was garbage — surface the friendly detail via a
  // rejected promise so callers hit their catch branch and toast the user
  // instead of silently receiving undefined fields.
  if (data._nonJson) {
    const err = new Error(data.detail || "Respuesta del servidor no válida.");
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

/**
 * Resolve a proof/attachment URL into something an <img src> can load.
 * - `data:` URLs are returned as-is (legacy base64 payloads).
 * - Absolute http(s) URLs are returned as-is.
 * - Relative `/api/files/...` paths (new Emergent object-storage links) are
 *   prefixed with the backend host. When the frontend and backend share an
 *   origin (production ag7store.online, preview *.emergentagent.com) the
 *   browser sends the httpOnly `ag7_token` cookie on the <img> request. When
 *   they don't, add `crossOrigin="use-credentials"` on the <img> at the call
 *   site — no per-URL token is exposed to JavaScript anymore.
 */
export function resolveFileUrl(url) {
  if (!url) return null;
  if (typeof url !== "string") return url;
  if (url.startsWith("data:")) return url;
  if (/^https?:\/\//i.test(url)) return url;
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  return url.startsWith("/") ? `${backend}${url}` : `${backend}/${url}`;
}

// ---------- AUTH ----------
export const register = (body) =>
  fetch(`${API_BASE}/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).then(handle);

export const login = (body) =>
  fetch(`${API_BASE}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).then(handle);

export const logout = () =>
  fetch(`${API_BASE}/auth/logout`, { method: "POST" }).then(handle);

export const me = () =>
  fetch(`${API_BASE}/auth/me`, { headers: authHeaders() }).then(handle);

export const getReferralSummary = () =>
  fetch(`${API_BASE}/referrals/me`, { headers: authHeaders() }).then(handle);

export const claimReferral = (referral_code) =>
  fetch(`${API_BASE}/referrals/claim`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ referral_code }),
  }).then(handle);

export const quoteReferralPrices = (items, quotedExchangeRate) =>
  fetch(`${API_BASE}/referrals/quote`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ items, quotedExchangeRate: Number(quotedExchangeRate) || 0 }),
  }).then(handle);

// ---------- RESELLER WITHDRAWALS ----------
export const requestWithdrawal = (payload) =>
  fetch(`${API_BASE}/withdrawals`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(payload),
  }).then(handle);

export const listMyWithdrawals = () =>
  fetch(`${API_BASE}/withdrawals/me`, { headers: authHeaders() }).then(handle);

export const listAdminWithdrawals = (status) =>
  fetch(`${API_BASE}/admin/withdrawals${status ? `?status=${encodeURIComponent(status)}` : ""}`, {
    headers: authHeaders(),
  }).then(handle);

export const updateWithdrawalStatus = (withdrawalId, { status, bankReference, proof }) => {
  const fd = new FormData();
  fd.append("status", status);
  if (bankReference) fd.append("bankReference", bankReference);
  if (proof) fd.append("proof", proof);
  return fetch(`${API_BASE}/admin/withdrawals/${withdrawalId}/status`, {
    method: "PUT",
    headers: authHeaders(), // no Content-Type: browser sets multipart boundary
    body: fd,
  }).then(handle);
};

// ---------- CATALOG ----------
// Always fetch fresh from the DB — never serve a stale/cached catalog.
export const listGames = ({ light = false } = {}) =>
  fetch(`${API_BASE}/games${light ? "?light=true" : ""}`, { cache: "no-store" }).then(handle);
export const listGamePackages = (gameId) =>
  fetch(`${API_BASE}/games/${gameId}/packages`, { cache: "no-store" }).then(handle);

export const createGame = (body) =>
  fetch(`${API_BASE}/games`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  }).then(handle);

export const editGame = (gameId, body) =>
  fetch(`${API_BASE}/games/${gameId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  }).then(handle);

export const deleteGame = (gameId) =>
  fetch(`${API_BASE}/games/${gameId}`, {
    method: "DELETE",
    headers: authHeaders(),
  }).then(handle);

export const addPackage = (body) =>
  fetch(`${API_BASE}/packages`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  }).then(handle);

export const editPackage = (gameId, pkgId, body) =>
  fetch(`${API_BASE}/packages/${gameId}/${pkgId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  }).then(handle);

export const deletePackage = (gameId, pkgId) =>
  fetch(`${API_BASE}/packages/${gameId}/${pkgId}`, {
    method: "DELETE",
    headers: authHeaders(),
  }).then(handle);

// ---------- PAYMENT METHODS ----------
export const listPaymentMethods = () =>
  fetch(`${API_BASE}/payment-methods`).then(handle);

export const adminListPaymentMethods = () =>
  fetch(`${API_BASE}/admin/payment-methods`, { headers: authHeaders() }).then(handle);

export const addPaymentMethod = (body) =>
  fetch(`${API_BASE}/payment-methods`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  }).then(handle);

export const editPaymentMethod = (id, body) =>
  fetch(`${API_BASE}/payment-methods/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  }).then(handle);

export const togglePaymentMethodActive = (id, active) =>
  fetch(`${API_BASE}/payment-methods/${id}/active`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ active }),
  }).then(handle);

export const deletePaymentMethod = (id) =>
  fetch(`${API_BASE}/payment-methods/${id}`, {
    method: "DELETE",
    headers: authHeaders(),
  }).then(handle);

// ---------- SETTINGS ----------
export const getSettings = () => fetch(`${API_BASE}/settings`, { cache: "no-store" }).then(handle);
export const getPricingVersion = () =>
  fetch(`${API_BASE}/pricing-version?t=${Date.now()}`, { cache: "no-store" }).then(handle);

export const updateExchangeRate = (exchange_rate) =>
  fetch(`${API_BASE}/settings/exchange-rate`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ exchange_rate }),
  }).then(handle);

// ---------- TASA_AG7 (automatización BCV) ----------
export const getExchangeRateStatus = () =>
  fetch(`${API_BASE}/admin/exchange-rate/status`, { headers: authHeaders() }).then(handle);

export const checkExchangeRateNow = () =>
  fetch(`${API_BASE}/admin/exchange-rate/check-now`, {
    method: "POST",
    headers: authHeaders(),
  }).then(handle);

export const enableExchangeRateAuto = () =>
  fetch(`${API_BASE}/admin/exchange-rate/auto`, {
    method: "POST",
    headers: authHeaders(),
  }).then(handle);


export const updateSupportWhatsapp = (support_whatsapp) =>
  fetch(`${API_BASE}/settings/support-whatsapp`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ support_whatsapp }),
  }).then(handle);

// ---------- ORDERS ----------
// `light: true` strips `verificationLog` from each row (huge JSON blob).
// Use it for polling; open the modal via `getOrder(id)` to load the full doc.
// `beforeCreatedAt` is the ISO cursor for the "Cargar más" pagination
// (returns rows strictly OLDER than that timestamp).
export const listOrders = ({ light = false, limit, beforeCreatedAt } = {}) => {
  const qs = new URLSearchParams();
  if (light) qs.set("light", "1");
  if (limit) qs.set("limit", String(limit));
  if (beforeCreatedAt) qs.set("before_created_at", beforeCreatedAt);
  const suffix = qs.toString() ? `?${qs}` : "";
  return fetch(`${API_BASE}/orders${suffix}`, { headers: authHeaders() }).then(handle);
};

// Full single-order doc — used by the detail modal / forensic report to
// pull the heavy `verificationLog` on demand.
export const getOrder = (orderId) =>
  fetch(`${API_BASE}/orders/${orderId}`, { headers: authHeaders() }).then(handle);

export const createOrder = async ({
  gameId,
  packageId,
  playerId,
  playerUsername,
  playerPassword,
  verifiedPlayerName,
  clientCi,
  paymentMethodId,
  paidAmountBs,
  referenceNumber,
  useCredit,
  proofFile,
}) => {
  const fd = new FormData();
  fd.append("gameId", gameId);
  fd.append("packageId", packageId);
  fd.append("playerId", playerId);
  if (playerUsername) fd.append("playerUsername", playerUsername);
  if (playerPassword) fd.append("playerPassword", playerPassword);
  if (verifiedPlayerName) fd.append("verifiedPlayerName", verifiedPlayerName);
  fd.append("clientCi", clientCi);
  fd.append("paymentMethodId", paymentMethodId);
  fd.append("paidAmountBs", String(paidAmountBs));
  fd.append("referenceNumber", referenceNumber);
  fd.append("useCredit", String(!!useCredit));
  fd.append("proof", proofFile);

  const res = await fetch(`${API_BASE}/orders`, {
    method: "POST",
    headers: authHeaders(),
    body: fd,
  });
  return handle(res);
};

export const createCartOrders = async ({
  items,
  clientCi,
  paymentMethodId,
  paidAmountBs,
  referenceNumber,
  useCredit,
  proofFile,
  quotedExchangeRate,
}) => {
  const fd = new FormData();
  fd.append("items", JSON.stringify(items));
  fd.append("clientCi", clientCi || "");
  fd.append("paymentMethodId", paymentMethodId || "");
  fd.append("paidAmountBs", String(paidAmountBs));
  fd.append("referenceNumber", referenceNumber || "");
  fd.append("useCredit", String(!!useCredit));
  fd.append("quotedExchangeRate", String(Number(quotedExchangeRate) || 0));
  if (proofFile) fd.append("proof", proofFile);

  const res = await fetch(`${API_BASE}/cart-orders`, {
    method: "POST",
    headers: authHeaders(),
    body: fd,
  });
  return handle(res);
};

export const updateOrderStatus = (orderId, status, rejectionReason, opts = {}) =>
  fetch(`${API_BASE}/orders/${orderId}/status`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({
      status,
      rejectionReason: rejectionReason || null,
      // Optional: when rejecting, admin can opt out of reclaiming an
      // auto-credited surplus by passing reclaimSurplus:false. Default true.
      reclaimSurplus: opts.reclaimSurplus !== false,
    }),
  }).then(handle);

export const confirmRecordedDeliveryByOrderNumber = (orderNumber) =>
  fetch(`${API_BASE}/admin/orders/by-number/${orderNumber}/confirm-recorded-delivery`, {
    method: "POST",
    headers: authHeaders(),
  }).then(handle);

export const approvePayment = (orderId) =>
  fetch(`${API_BASE}/orders/${orderId}/approve-payment`, {
    method: "POST",
    headers: authHeaders(),
  }).then(handle);

// Admin overrides AI-extracted values with hand-typed ones from the physical
// receipt and finalises the verdict (APPROVE or REJECT). See
// `/orders/{id}/manual-verify` in server.py.
export const manualVerifyPayment = (orderId, payload) =>
  fetch(`${API_BASE}/orders/${orderId}/manual-verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(payload),
  }).then(handle);

// Admin edits the wholesale cost of an order (per-order provider discount).
// The backend distributes the new cost across items proportionally and
// re-mirrors the sale to Supabase so the Ganancias dashboard picks up the
// new margin instantly.
export const updateOrderCost = (orderId, cost) =>
  fetch(`${API_BASE}/orders/${orderId}/cost`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ cost: Number(cost) }),
  }).then(handle);

// ─── Admin / Sales (Supabase-backed) ─────────────────────────────
export const getSalesDaily = () =>
  fetch(`${API_BASE}/admin/sales/daily`, { headers: authHeaders() }).then(handle);

export const getSalesRecent = (limit = 50) =>
  fetch(`${API_BASE}/admin/sales/recent?limit=${limit}`, { headers: authHeaders() }).then(handle);

// ─── Admin / Financial reports (Mongo historical snapshots) ───────────────
const financialReportQuery = (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => { if (value) params.set(key, value); });
  return params.toString();
};
export const getFinancialReport = (filters = {}) =>
  fetch(`${API_BASE}/admin/financial-reports?${financialReportQuery(filters)}`, { headers: authHeaders() }).then(handle);
export const exportFinancialReport = async (filters = {}) => {
  const response = await fetch(`${API_BASE}/admin/financial-reports/export?${financialReportQuery(filters)}`, { headers: authHeaders() });
  if (!response.ok) throw new Error((await response.json().catch(() => ({}))).detail || "No se pudo exportar el reporte");
  return { blob: await response.blob(), filename: response.headers.get("content-disposition")?.match(/filename="?([^";]+)"?/)?.[1] || "ag7-reporte-financiero.xlsx" };
};

// ─────────────── ORDER ADJUSTMENT WORKFLOW ─────────────────────────────
export const requestOrderAdjustment = (orderId, payload) =>
  fetch(`${API_BASE}/orders/${orderId}/request-adjustment`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(payload),
  }).then(handle);

export const submitAdjustmentPayment = (orderId, { paymentMethodId, paidAmountBs, referenceNumber, proof, useCreditBs }) => {
  const fd = new FormData();
  fd.append("paymentMethodId", paymentMethodId || "");
  fd.append("paidAmountBs", String(paidAmountBs || 0));
  fd.append("referenceNumber", referenceNumber || "");
  fd.append("useCreditBs", String(useCreditBs || 0));
  if (proof) fd.append("proof", proof);
  return fetch(`${API_BASE}/orders/${orderId}/submit-adjustment-payment`, {
    method: "POST",
    headers: authHeaders(), // no Content-Type: browser sets multipart boundary
    body: fd,
  }).then(handle);
};

export const acceptOrderAdjustment = (orderId) =>
  fetch(`${API_BASE}/orders/${orderId}/accept-adjustment`, {
    method: "POST",
    headers: authHeaders(),
  }).then(handle);

export const rejectOrderAdjustment = (orderId, reason = "") =>
  fetch(`${API_BASE}/orders/${orderId}/reject-adjustment`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ reason }),
  }).then(handle);

export const approveOrderAdjustment = (orderId) =>
  fetch(`${API_BASE}/orders/${orderId}/approve-adjustment`, {
    method: "POST",
    headers: authHeaders(),
  }).then(handle);

export const completePartialPayment = async ({
  orderId,
  paidAmountBs,
  paidAmountUsd,
  paymentCurrency,
  quotedExchangeRate,
  proofFile,
  paymentMethodId,
  useCredit = false,
}) => {
  const fd = new FormData();
  if (paidAmountBs != null) fd.append("paidAmountBs", String(paidAmountBs));
  if (paidAmountUsd != null) fd.append("paidAmountUsd", String(paidAmountUsd));
  if (paymentCurrency) fd.append("paymentCurrency", paymentCurrency);
  if (quotedExchangeRate != null) fd.append("quotedExchangeRate", String(quotedExchangeRate));
  if (proofFile) fd.append("proof", proofFile);
  if (paymentMethodId) fd.append("paymentMethodId", paymentMethodId);
  fd.append("useCredit", useCredit ? "true" : "false");
  const res = await fetch(`${API_BASE}/orders/${orderId}/complete-payment`, {
    method: "POST",
    headers: authHeaders(),
    body: fd,
  });
  return handle(res);
};

export const markWelcomeSeen = () =>
  fetch(`${API_BASE}/auth/mark-welcome-seen`, {
    method: "POST",
    headers: authHeaders(),
  }).then(handle);

// ---------- ADMIN: TOP USERS RANKING ----------
export const getAdminTopUsers = (range = "all", limit = 100) =>
  fetch(`${API_BASE}/admin/top-users?range=${encodeURIComponent(range)}&limit=${limit}`, {
    headers: authHeaders(),
  }).then(handle);

export const adminResetTestData = (confirm) =>
  fetch(`${API_BASE}/admin/reset-test-data`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ confirm }),
  }).then(handle);

// ---------- PUBLIC ----------
export const getPublicStats = () =>
  fetch(`${API_BASE}/public/stats`).then(handle);

export const getSocialProofRecent = (limit = 12) =>
  fetch(`${API_BASE}/social-proof/recent?limit=${limit}`).then(handle);

export const verifyFreeFirePlayer = (uid) =>
  fetch(`${API_BASE}/verify-ff-id?uid=${encodeURIComponent(uid)}`).then(handle);

export const verifyFreeFirePlayerFull = (playerId) =>
  fetch(`${API_BASE}/freefire/verify-player`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ player_id: playerId }),
  }).then(handle);

export const uploadSupportScreenshot = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return fetch(`${API_BASE}/support/upload-screenshot`, {
    method: "POST",
    headers: authHeaders(), // no Content-Type: browser sets multipart boundary
    body: fd,
  }).then(handle);
};

export const verifyTwoFactor = (body) =>
  fetch(`${API_BASE}/auth/verify-2fa`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).then(handle);

export const forgotPassword = (email) =>
  fetch(`${API_BASE}/auth/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  }).then(handle);

// New 3-step phone OTP flow
export const passwordResetRequest = (phone_number) =>
  fetch(`${API_BASE}/auth/password-reset/request`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ phone_number }),
  }).then(handle);

export const passwordResetVerify = (phone_number, code) =>
  fetch(`${API_BASE}/auth/password-reset/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ phone_number, code }),
  }).then(handle);

export const passwordResetConfirm = (reset_token, new_password) =>
  fetch(`${API_BASE}/auth/password-reset/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reset_token, new_password }),
  }).then(handle);

export const changePassword = (current_password, new_password) =>
  fetch(`${API_BASE}/auth/change-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ current_password, new_password }),
  }).then(handle);

export const twoFactorStatus = () =>
  fetch(`${API_BASE}/auth/2fa/status`, { headers: authHeaders() }).then(handle);

export const twoFactorSetup = () =>
  fetch(`${API_BASE}/auth/2fa/setup`, {
    method: "POST",
    headers: authHeaders(),
  }).then(handle);

export const twoFactorEnable = (code) =>
  fetch(`${API_BASE}/auth/2fa/enable`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ code }),
  }).then(handle);

export const twoFactorDisable = (current_password) =>
  fetch(`${API_BASE}/auth/2fa/disable`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ current_password }),
  }).then(handle);

// ---------- ADMIN: USERS ----------
export const listUsers = () =>
  fetch(`${API_BASE}/admin/users`, { headers: authHeaders() }).then(handle);

export const updateUserStatus = (userId, status, reason) =>
  fetch(`${API_BASE}/admin/users/${userId}/status`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ status, reason: reason || null }),
  }).then(handle);

export const updateUserRole = (userId, role) =>
  fetch(`${API_BASE}/admin/users/${userId}/role`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ role }),
  }).then(handle);

export const acknowledgeOrder = (orderId) =>
  fetch(`${API_BASE}/orders/${orderId}/acknowledge`, {
    method: "POST",
    headers: authHeaders(),
  }).then(handle);

export const acknowledgeOrdersBatch = (orderIds) =>
  fetch(`${API_BASE}/orders/acknowledge-batch`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ orderIds }),
  }).then(handle);

// ---------- AG7 TORNEOS ----------
export const listTournaments = () => fetch(`${API_BASE}/tournaments`, { headers: authHeaders() }).then(handle);
export const getTournament = (id) => fetch(`${API_BASE}/tournaments/${id}`, { headers: authHeaders() }).then(handle);
export const myTournamentRegistrations = () => fetch(`${API_BASE}/tournaments/me/registrations`, { headers: authHeaders() }).then(handle);
export const registerTournament = (id, body) => fetch(`${API_BASE}/tournaments/${id}/registrations`, {
  method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify(body),
}).then(handle);
export const submitTournamentPayment = (id, formData) => fetch(`${API_BASE}/tournament-registrations/${id}/submit-payment`, {
  method: "POST", headers: authHeaders(), body: formData,
}).then(handle);
export const withdrawTournamentRegistration = (id, body) => fetch(`${API_BASE}/tournament-registrations/${id}/withdraw`, {
  method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify(body),
}).then(handle);
export const getTournamentRoom = (id) => fetch(`${API_BASE}/tournament-registrations/${id}/room`, { headers: authHeaders() }).then(handle);
export const adminListTournaments = () => fetch(`${API_BASE}/admin/tournaments`, { headers: authHeaders() }).then(handle);
export const adminCreateTournament = (body) => fetch(`${API_BASE}/admin/tournaments`, { method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify(body) }).then(handle);
export const adminUploadTournamentCover = (id, file) => { const form = new FormData(); form.append("file", file); return fetch(`${API_BASE}/admin/tournaments/${id}/cover`, { method: "POST", headers: authHeaders(), body: form }).then(handle); };
export const adminUpdateTournament = (id, body) => fetch(`${API_BASE}/admin/tournaments/${id}`, { method: "PUT", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify(body) }).then(handle);
export const adminDeleteTournament = (id) => fetch(`${API_BASE}/admin/tournaments/${id}`, { method: "DELETE", headers: authHeaders() }).then(handle);
export const adminTournamentStatus = (id, status) => { const form = new FormData(); form.append("status", status); return fetch(`${API_BASE}/admin/tournaments/${id}/status`, { method: "PUT", headers: authHeaders(), body: form }).then(handle); };
export const adminTournamentRegistrations = (id) => fetch(`${API_BASE}/admin/tournaments/${id}/registrations`, { headers: authHeaders() }).then(handle);
export const adminTournamentPaymentDecision = (id, body) => fetch(`${API_BASE}/admin/tournament-registrations/${id}/payment`, { method: "PUT", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify(body) }).then(handle);
export const adminTournamentRefundDecision = (id, body) => fetch(`${API_BASE}/admin/tournament-registrations/${id}/refund`, { method: "PUT", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify(body) }).then(handle);
export const adminTournamentRoom = (id, body) => fetch(`${API_BASE}/admin/tournaments/${id}/room`, { method: "PUT", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify(body) }).then(handle);
export const adminTournamentResults = (id, body) => fetch(`${API_BASE}/admin/tournaments/${id}/results`, { method: "PUT", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify(body) }).then(handle);

// ---------- ORDER CHAT ----------
export const listOrderMessages = (orderId) =>
  fetch(`${API_BASE}/orders/${orderId}/messages`, { headers: authHeaders() }).then(handle);

export const sendOrderMessage = (orderId, message, attachment_url = null) =>
  fetch(`${API_BASE}/orders/${orderId}/messages`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ message, attachment_url }),
  }).then(handle);

// ---------- SOCIAL NETWORKS ----------
export const listSocialNetworks = () =>
  fetch(`${API_BASE}/social-networks`).then(handle);

export const adminListSocialNetworks = () =>
  fetch(`${API_BASE}/admin/social-networks`, { headers: authHeaders() }).then(handle);

export const adminCreateSocialNetwork = (body) =>
  fetch(`${API_BASE}/admin/social-networks`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  }).then(handle);

export const adminUpdateSocialNetwork = (id, body) =>
  fetch(`${API_BASE}/admin/social-networks/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  }).then(handle);

export const adminDeleteSocialNetwork = (id) =>
  fetch(`${API_BASE}/admin/social-networks/${id}`, {
    method: "DELETE",
    headers: authHeaders(),
  }).then(handle);

// ---------- ADMIN: user history ----------
export const adminUserHistory = (userId) =>
  fetch(`${API_BASE}/admin/users/${userId}/history`, { headers: authHeaders() }).then(handle);

// ---------- SITE OPEN/CLOSED ----------
export const getSiteStatus = () =>
  fetch(`${API_BASE}/site-status`).then(handle);
export const adminActivityPing = () =>
  fetch(`${API_BASE}/admin/activity`, { method: "POST", headers: authHeaders() }).then(handle);
export const adminAutoCloseStatus = () =>
  fetch(`${API_BASE}/admin/auto-close`, { headers: authHeaders() }).then(handle);
export const adminAutoCloseUpdate = (patch) =>
  fetch(`${API_BASE}/admin/auto-close`, {
    method: "PUT", headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  }).then(handle);
export const adminAutoCloseException = (minutes) =>
  fetch(`${API_BASE}/admin/auto-close/exception`, {
    method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ minutes }),
  }).then(handle);

export const adminSetSiteStatus = (open, reason = "") =>
  fetch(`${API_BASE}/admin/site-status`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ open, reason }),
  }).then(handle);

// ---------- GAME BANNERS ----------
export const getActiveBanner = (gameId) =>
  fetch(`${API_BASE}/games/${gameId}/banner/active`).then(handle);

export const adminListBanners = () =>
  fetch(`${API_BASE}/admin/banners`, { headers: authHeaders() }).then(handle);

export const adminCreateBanner = (body) =>
  fetch(`${API_BASE}/admin/banners`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  }).then(handle);

export const adminUpdateBanner = (id, body) =>
  fetch(`${API_BASE}/admin/banners/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  }).then(handle);

export const adminDeleteBanner = (id) =>
  fetch(`${API_BASE}/admin/banners/${id}`, {
    method: "DELETE",
    headers: authHeaders(),
  }).then(handle);

export const deleteUser = (userId) =>
  fetch(`${API_BASE}/admin/users/${userId}`, {
    method: "DELETE",
    headers: authHeaders(),
  }).then(handle);

// ---------- CATALOG EXPORT / IMPORT ----------
export const adminExportCatalog = () =>
  fetch(`${API_BASE}/admin/catalog/export`, { headers: authHeaders(), cache: "no-store" }).then(handle);

export const adminImportCatalog = (payload, apply) =>
  fetch(`${API_BASE}/admin/catalog/import?apply=${apply ? "true" : "false"}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(payload),
  }).then(handle);

// ---------- WEB PUSH ----------
export const getPushVapidKey = () =>
  fetch(`${API_BASE}/push/vapid-public-key`, { cache: "no-store" }).then(handle);

export const pushSubscribe = (subscription) =>
  fetch(`${API_BASE}/admin/push/subscribe`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(subscription),
  }).then(handle);

export const pushUnsubscribe = (endpoint) =>
  fetch(`${API_BASE}/admin/push/subscribe?endpoint=${encodeURIComponent(endpoint)}`, {
    method: "DELETE",
    headers: authHeaders(),
  }).then(handle);

export const pushStatus = (endpoint) => {
  const q = endpoint ? `?endpoint=${encodeURIComponent(endpoint)}` : "";
  return fetch(`${API_BASE}/admin/push/status${q}`, { headers: authHeaders(), cache: "no-store" }).then(handle);
};

export const pushTest = () =>
  fetch(`${API_BASE}/admin/push/test`, { method: "POST", headers: authHeaders() }).then(handle);

// Public: subscribe the current browser to the "store reopens" waitlist.
// No auth required — used by the closed-store banner ("Avisarme al abrir").
export const pushWaitlistSubscribe = (subscription) =>
  fetch(`${API_BASE}/push/waitlist`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(subscription),
  }).then(handle);

export const pushWaitlistSize = () =>
  fetch(`${API_BASE}/admin/push/waitlist-size`, { headers: authHeaders(), cache: "no-store" }).then(handle);

// ---------- ADMIN: AUTOMATIZACIÓN DE PROVEEDOR (Free Fire · Inefable) ----------
const PA_BASE = `${API_BASE}/admin/provider-automation`;

export const paStatus = () =>
  fetch(`${PA_BASE}/status`, { headers: authHeaders(), cache: "no-store" }).then(handle);

export const paFreeFireRoutes = () =>
  fetch(`${PA_BASE}/free-fire-routes`, { headers: authHeaders(), cache: "no-store" }).then(handle);
export const paFreeFireNicknameProvider = () =>
  fetch(`${PA_BASE}/free-fire-nickname-provider`, { headers: authHeaders(), cache: "no-store" }).then(handle);
export const paSetFreeFireNicknameProvider = (provider) =>
  fetch(`${PA_BASE}/free-fire-nickname-provider`, { method: "PUT", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify({ provider }) }).then(handle);

export const paSetFreeFireRoutes = (settings) =>
  fetch(`${PA_BASE}/free-fire-routes`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(settings),
  }).then(handle);

export const paRefreshFreeFirePrices = () =>
  fetch(`${PA_BASE}/free-fire-prices/refresh`, {
    method: "POST", headers: authHeaders(),
  }).then(handle);

export const paFreeFirePriceAdjustments = () =>
  fetch(`${PA_BASE}/free-fire-price-adjustments`, {
    headers: authHeaders(), cache: "no-store",
  }).then(handle);

export const paApplyFreeFirePriceAdjustment = (payload) =>
  fetch(`${PA_BASE}/free-fire-price-adjustments/apply`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(payload),
  }).then(handle);

export const paOperations = ({ scope = "", status = "", search = "", limit = 100 } = {}) => {
  const qs = new URLSearchParams();
  if (scope) qs.set("scope", scope);
  if (status) qs.set("status", status);
  if (search) qs.set("search", search);
  qs.set("limit", String(limit));
  return fetch(`${PA_BASE}/operations?${qs.toString()}`, {
    headers: authHeaders(), cache: "no-store",
  }).then(handle);
};

export const paOperation = (id) =>
  fetch(`${PA_BASE}/operations/${id}`, { headers: authHeaders(), cache: "no-store" }).then(handle);

export const paExecute = (id) =>
  fetch(`${PA_BASE}/operations/${id}/execute`, {
    method: "POST", headers: authHeaders(),
  }).then(handle);

export const paRetry = (id) =>
  fetch(`${PA_BASE}/operations/${id}/retry`, {
    method: "POST", headers: authHeaders(),
  }).then(handle);

export const paResume = (id) =>
  fetch(`${PA_BASE}/operations/${id}/resume`, {
    method: "POST", headers: authHeaders(),
  }).then(handle);

export const paStopBloodStrikeReconciliation = (id) =>
  fetch(`${PA_BASE}/operations/${id}/stop-reconciliation`, {
    method: "POST", headers: authHeaders(),
  }).then(handle);

export const paPolicies = () =>
  fetch(`${PA_BASE}/policies`, { headers: authHeaders() }).then(handle);

export const paShadowRehearsal = (productCode, playerId) =>
  fetch(`${PA_BASE}/shadow-rehearsal`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ product_code: productCode, player_id: playerId }),
  }).then(handle);

export const paAutomateItem = (orderId, itemId) =>
  fetch(`${API_BASE}/admin/orders/${orderId}/items/${itemId}/automate`, {
    method: "POST", headers: authHeaders(),
  }).then(handle);

export const paOrderAutomation = (orderId) =>
  fetch(`${API_BASE}/admin/orders/${orderId}/automation`, { headers: authHeaders() }).then(handle);

export const paReevaluate = (id) =>
  fetch(`${PA_BASE}/operations/${id}/reevaluate`, {
    method: "POST", headers: authHeaders(),
  }).then(handle);

export const paStop = (reason = "") =>
  fetch(`${PA_BASE}/stop`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ reason }),
  }).then(handle);

export const paStart = () =>
  fetch(`${PA_BASE}/start`, { method: "POST", headers: authHeaders() }).then(handle);

export const paResetBreaker = () =>
  fetch(`${PA_BASE}/circuit-breaker/reset`, { method: "POST", headers: authHeaders() }).then(handle);

export const paSetMode = (mode) =>
  fetch(`${PA_BASE}/mode`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ mode }),
  }).then(handle);

export const paNotifications = () =>
  fetch(`${PA_BASE}/notifications`, { headers: authHeaders(), cache: "no-store" }).then(handle);

// 💳 Aprobación financiera MANUAL del pago (única llave de la automatización).
export const approveOrderPayment = (orderId) =>
  fetch(`${API_BASE}/orders/${orderId}/payment-approval`, {
    method: "POST", headers: authHeaders(),
  }).then(handle);
